import { authService } from '../auth-config/auth-service';
import type {
  ChatSession,
  ChatMessage,
  PagedMessages,
  SendMessageResponse,
} from '../auth-config/types';

// ─────────────────────────────────────────────────────────────────────────────
// Chat API service
// All requests use the Cognito Bearer token via authService.getAccessToken()
// ─────────────────────────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await authService.getAccessToken();
  if (!token) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// POST api/chat/message
// Send a message; creates the session if it does not exist; returns AI reply.
export async function sendMessage(
  SubjectId: number,
  StandardId: number,
  Message: string
): Promise<SendMessageResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch('/api/chat/message', {
    method: 'POST',
    headers,
    body: JSON.stringify({ SubjectId, StandardId, Message }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Send message failed: ${err}`);
  }
  return res.json();
}

// GET api/chat/sessions
// List all chat sessions for the authenticated user.
export async function getSessions(): Promise<ChatSession[]> {
  const headers = await getAuthHeaders();
  const res = await fetch('/api/chat/sessions', { headers });
  if (!res.ok) throw new Error('Failed to fetch chat sessions');
  return res.json();
}

// GET api/chat/sessions/{sessionId}/messages?page=1&pageSize=20
// Paginated message history for a session (newest first, page 1 = most recent).
export async function getMessages(
  sessionId: string,
  page = 1,
  pageSize = 20
): Promise<PagedMessages> {
  const headers = await getAuthHeaders();
  const url = `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages?page=${page}&pageSize=${pageSize}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error('Failed to fetch chat messages');
  const data = await res.json();

  // Normalise: the API returns newest-first, but the UI needs
  // them in chronological order (oldest → newest). Reverse each page
  // before handing to the caller.
  const messages: ChatMessage[] = Array.isArray(data.messages)
    ? [...data.messages].reverse()
    : Array.isArray(data)
    ? [...data].reverse()
    : [];

  const hasMore: boolean =
    typeof data.hasMore === 'boolean'
      ? data.hasMore
      : messages.length === pageSize;

  return { messages, page, pageSize, hasMore };
}

export const chatService = { sendMessage, getSessions, getMessages };
